import { type FC } from 'react'
import { canExpand, getUiOptions, type ObjectFieldTemplatePropertyType, type ObjectFieldTemplateProps, type RJSFSchema } from '@rjsf/utils'
import { type ParametersFormContext } from '../formContext'
import { isPlainObject, type JsonObject } from '../mergePatch'
import { type ParameterSectionConfig } from '../uiSchema'
import { AddItemLink } from './AddItemLink'
import { headingOf } from './displayLabel'
import { GroupHeading } from './GroupHeading'
import { ParameterSection } from './ParameterSection'

interface PropertyGroup {
  key: string
  section: ParameterSectionConfig | null
  properties: ObjectFieldTemplatePropertyType[]
}

const isSectionConfig = (value: unknown): value is ParameterSectionConfig =>
  isPlainObject(value) &&
  typeof value.title === 'string' &&
  typeof value.start === 'string' &&
  (value.description === undefined || typeof value.description === 'string')

const groupsOf = (properties: ObjectFieldTemplatePropertyType[], sections: ParameterSectionConfig[], listed: unknown[]): PropertyGroup[] => {
  const isListed = (property: ObjectFieldTemplatePropertyType) => listed.includes(property.name)
  const sectionOf = (property: ObjectFieldTemplatePropertyType): ParameterSectionConfig | null =>
    sections.filter((section) => listed.indexOf(section.start) <= listed.indexOf(property.name)).pop() ?? null
  const sectioned = properties.filter(isListed).reduce<PropertyGroup[]>((groups, property) => {
    const section = sectionOf(property)
    const last = groups[groups.length - 1]
    return last?.section === section
      ? [...groups.slice(0, -1), { ...last, properties: [...last.properties, property] }]
      : [...groups, { key: section ? `section:${section.start}` : 'leading', section, properties: [property] }]
  }, [])
  return [...sectioned, { key: 'trailing', section: null, properties: properties.filter((property) => !isListed(property)) }].filter(
    (group) => group.properties.length > 0,
  )
}

export const ObjectFieldTemplate: FC<ObjectFieldTemplateProps<JsonObject, RJSFSchema, ParametersFormContext>> = ({
  title,
  description,
  properties,
  idSchema,
  schema,
  uiSchema,
  formData,
  onAddClick,
  disabled,
  readonly,
  registry,
}) => {
  const isRoot = idSchema.$id === registry.formContext.rootId
  const options = getUiOptions(uiSchema)
  const sections = Array.isArray(options.zocksSections) ? options.zocksSections.filter(isSectionConfig) : []
  const listed: unknown[] = Array.isArray(options.order) ? options.order : []
  const fields = (groupProperties: ObjectFieldTemplatePropertyType[]) => groupProperties.map((property) => <div key={property.name}>{property.content}</div>)
  const content = (
    <>
      {sections.length > 0
        ? groupsOf(properties, sections, listed).map((group, index) => (
            <ParameterSection key={group.key} heading={group.section} divider={index > 0}>
              {fields(group.properties)}
            </ParameterSection>
          ))
        : fields(properties)}
      {canExpand(schema, uiSchema, formData) && !readonly ? (
        <AddItemLink noun='property' className='mt-2' disabled={disabled} onClick={onAddClick(schema)} />
      ) : null}
    </>
  )

  if (isRoot || options.zocksItemLayout === 'expandable') {
    return <div className='d-flex flex-column'>{content}</div>
  }

  return (
    <div className='d-flex flex-column border zocks-border-color-gray-300 rounded-3 px-3 pt-3'>
      <GroupHeading title={headingOf(schema, title)} description={description || schema.description} className='mb-2' />
      {content}
    </div>
  )
}
